import { useState, useEffect, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  createWorkoutSession,
  fetchWorkoutSession,
  updateWorkoutSession,
  deleteWorkoutSession,
  fetchUserWorkoutSessions,
  searchWorkoutSessions,
  setCurrentSession,
  setActiveSession,
  updateActiveSession,
  resetSessions,
  updateWorkoutSessionExerciseMetrics,
  addWorkoutSessionResult,
  selectWorkoutSessions,
  selectCurrentSession,
  selectActiveSession,
  selectRecentSessions,
  selectIsLoading,
  selectError
} from '../store/slices/workoutSessionSlice';
import {
  WorkoutSessionBase,
  WorkoutSession,
  WorkoutSessionSearchParams,
  ActiveWorkoutSession,
  WorkoutSessionMetric,
  WorkoutSessionSet
} from '../types/workout-session';
import { WorkoutRoutine } from '../types/workout-routine';
import * as workoutSessionService from '../services/firebase/workout-session.service';
import { useAuth } from './useAuth';

/**
 * Custom hook for managing workout sessions
 */
export const useWorkoutSession = () => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  
  // Subscription cleanup references
  const sessionsUnsubRef = useRef<(() => void) | null>(null);
  const currentSessionUnsubRef = useRef<(() => void) | null>(null);
  
  // Interval reference for timer
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Selectors
  const sessions = useAppSelector(selectWorkoutSessions);
  const currentSession = useAppSelector(selectCurrentSession);
  const activeSession = useAppSelector(selectActiveSession);
  const recentSessions = useAppSelector(selectRecentSessions);

  // Live mirror of activeSession for closures that outlive a render (the
  // timer interval). Reading `activeSession` directly inside setInterval
  // froze the captured snapshot, so every tick re-dispatched the same
  // elapsedTime and the countdown never advanced.
  const activeSessionRef = useRef(activeSession);
  useEffect(() => {
    activeSessionRef.current = activeSession;
  }, [activeSession]);
  const storeLoading = useAppSelector(selectIsLoading);
  const storeError = useAppSelector(selectError);
  
  /**
   * Initialize session subscriptions
   */
  const initializeSubscriptions = useCallback(() => {
    if (!user?.uid) return;
    
    try {
      // Clean up any existing subscriptions
      if (sessionsUnsubRef.current) {
        sessionsUnsubRef.current();
        sessionsUnsubRef.current = null;
      }
      
      // Subscribe to user's sessions
      sessionsUnsubRef.current = workoutSessionService.subscribeToUserWorkoutSessions(
        user.uid,
        (sessions) => {
          sessions.forEach(session => {
            dispatch(addWorkoutSessionResult(session));
          });
        }
      );
    } catch (error: any) {
      setLocalError(error.message || 'Failed to initialize session subscriptions');
    }
  }, [user?.uid, dispatch]);
  
  /**
   * Clean up subscriptions on unmount
   */
  useEffect(() => {
    return () => {
      if (sessionsUnsubRef.current) {
        sessionsUnsubRef.current();
        sessionsUnsubRef.current = null;
      }
      
      if (currentSessionUnsubRef.current) {
        currentSessionUnsubRef.current();
        currentSessionUnsubRef.current = null;
      }
      
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);
  
  /**
   * Subscribe to a specific session
   */
  const subscribeToSession = useCallback((
    userId: string,
    sessionKey: string
  ): (() => void) => {
    if (currentSessionUnsubRef.current) {
      currentSessionUnsubRef.current();
      currentSessionUnsubRef.current = null;
    }
    
    const unsubscribe = workoutSessionService.subscribeToWorkoutSession(
      userId,
      sessionKey,
      (session) => {
        if (session) {
          dispatch(setCurrentSession(session));
        }
      }
    );
    
    currentSessionUnsubRef.current = unsubscribe;
    return unsubscribe;
  }, [dispatch]);
  
  /**
   * Get session by ID
   */
  const getSessionById = useCallback(async (
    userId: string,
    sessionKey: string
  ) => {
    if (!userId || !sessionKey) return;
    
    try {
      setLocalLoading(true);
      await dispatch(fetchWorkoutSession({ userId, sessionKey }));
      setLocalLoading(false);
    } catch (error: any) {
      setLocalError(error.message || 'Failed to get session');
      setLocalLoading(false);
    }
  }, [dispatch]);
  
  /**
   * Create a new workout session
   */
  const createSession = useCallback(async (
    sessionData: WorkoutSessionBase
  ): Promise<string | undefined> => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return;
    }
    
    try {
      setLocalLoading(true);
      const resultAction = await dispatch(createWorkoutSession({
        userId: user.uid,
        sessionData
      }));
      setLocalLoading(false);
      
      if (createWorkoutSession.fulfilled.match(resultAction)) {
        return resultAction.payload;
      } else {
        setLocalError(resultAction.payload as string || 'Failed to create session');
        return;
      }
    } catch (error: any) {
      setLocalError(error.message || 'Failed to create session');
      setLocalLoading(false);
      return;
    }
  }, [user?.uid, dispatch]);
  
  /**
   * Update an existing workout session
   */
  const updateSession = useCallback(async (
    sessionKey: string,
    updates: Partial<WorkoutSessionBase>
  ): Promise<boolean> => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return false;
    }
    
    try {
      setLocalLoading(true);
      const resultAction = await dispatch(updateWorkoutSession({
        userId: user.uid,
        sessionKey,
        updates
      }));
      setLocalLoading(false);
      
      return updateWorkoutSession.fulfilled.match(resultAction);
    } catch (error: any) {
      setLocalError(error.message || 'Failed to update session');
      setLocalLoading(false);
      return false;
    }
  }, [user?.uid, dispatch]);
  
  /**
   * Delete a workout session
   */
  const deleteSession = useCallback(async (
    sessionKey: string
  ): Promise<boolean> => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return false;
    }
    
    try {
      setLocalLoading(true);
      const resultAction = await dispatch(deleteWorkoutSession({
        userId: user.uid,
        sessionKey
      }));
      setLocalLoading(false);
      
      return deleteWorkoutSession.fulfilled.match(resultAction);
    } catch (error: any) {
      setLocalError(error.message || 'Failed to delete session');
      setLocalLoading(false);
      return false;
    }
  }, [user?.uid, dispatch]);
  
  /**
   * Fetch user's workout sessions
   */
  const loadUserSessions = useCallback(async (
    limit?: number
  ): Promise<boolean> => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return false;
    }
    
    try {
      setLocalLoading(true);
      const resultAction = await dispatch(fetchUserWorkoutSessions({
        userId: user.uid,
        limit
      }));
      setLocalLoading(false);
      
      return fetchUserWorkoutSessions.fulfilled.match(resultAction);
    } catch (error: any) {
      setLocalError(error.message || 'Failed to load sessions');
      setLocalLoading(false);
      return false;
    }
  }, [user?.uid, dispatch]);
  
  /**
   * Search workout sessions
   */
  const searchSessions = useCallback(async (
    searchParams: WorkoutSessionSearchParams
  ): Promise<WorkoutSession[]> => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return [];
    }
    
    try {
      setLocalLoading(true);
      const resultAction = await dispatch(searchWorkoutSessions({
        userId: user.uid,
        searchParams
      }));
      setLocalLoading(false);
      
      if (searchWorkoutSessions.fulfilled.match(resultAction)) {
        return resultAction.payload;
      } else {
        setLocalError(resultAction.payload as string || 'Failed to search sessions');
        return [];
      }
    } catch (error: any) {
      setLocalError(error.message || 'Failed to search sessions');
      setLocalLoading(false);
      return [];
    }
  }, [user?.uid, dispatch]);
  
  /**
   * Filter sessions by criteria
   */
  const filterSessions = useCallback((
    sessionKeys: string[],
    searchParams: WorkoutSessionSearchParams
  ): WorkoutSession[] => {
    let filteredSessions = sessionKeys
      .map(key => sessions[key])
      .filter(Boolean);
    
    if (searchParams.keyword) {
      const keyword = searchParams.keyword.toLowerCase();
      filteredSessions = filteredSessions.filter(session => {
        // Check if keyword appears in notes
        if (session.notes && session.notes.toLowerCase().includes(keyword)) {
          return true;
        }
        
        // Check if keyword appears in tags
        if (session.tags && session.tags.some(tag => tag.toLowerCase().includes(keyword))) {
          return true;
        }
        
        // Check if keyword appears in exercise names
        return session.sets.some(set =>
          set.exercises.some(exercise =>
            exercise.name.toLowerCase().includes(keyword)
          )
        );
      });
    }
    
    if (searchParams.tag) {
      filteredSessions = filteredSessions.filter(session =>
        session.tags && session.tags.includes(searchParams.tag as string)
      );
    }
    
    // Sort by specified property
    if (searchParams.orderByProp) {
      filteredSessions.sort((a, b) => {
        const aValue = a[searchParams.orderByProp as keyof WorkoutSession] as number;
        const bValue = b[searchParams.orderByProp as keyof WorkoutSession] as number;
        
        if (searchParams.orderDirection === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });
    } else {
      // Default sort by date (newest first)
      filteredSessions.sort((a, b) => b.dateCreated - a.dateCreated);
    }
    
    return filteredSessions;
  }, [sessions]);
  
  /**
   * Initialize active workout session
   */
  const startWorkoutSession = useCallback((
    routine: WorkoutRoutine
  ): void => {
    if (!routine || !routine.sets || routine.sets.length === 0) {
      setLocalError('Invalid routine');
      return;
    }
    
    const strippedSets: WorkoutSessionSet[] = routine.sets.map(set => ({
      title: set.title,
      exercises: set.exercises.map(exercise => ({
        name: exercise.name || exercise.title,
        metrics: exercise.metrics ? exercise.metrics.map(metric => ({
          name: metric.name,
          um: metric.unit || 'count', // um (unit of measurement) from unit
          valueType: typeof metric.value === 'number' ? 'number' : 'string',
          value: metric.value
        })) : []
      }))
    }));
    
    const firstSet = routine.sets[0];
    const firstExercise = firstSet?.exercises[0];
    const hasTimeMetric = firstExercise?.metrics && firstExercise.metrics.some(m => m.name === 'Time');
    let countdownTime = 0;

    if (hasTimeMetric && firstExercise && firstExercise.metrics) {
      const timeMetric = firstExercise.metrics.find(m => m.name === 'Time');
      if (timeMetric) {
        countdownTime = Number(timeMetric.value) || 0;
      }
    }
    
    const activeSessionData: ActiveWorkoutSession = {
      elapsedTime: 0,
      isRunning: false,
      didStart: false,
      didFinish: false,
      currentSetIndex: 0,
      currentExerciseIndex: 0,
      exercisesCount: firstSet?.exercises.length || 0,
      setsCount: routine.sets?.length || 0,
      setExercisesResults: strippedSets,
      hasTimeMetric: hasTimeMetric || false,
      countdownTime
    };
    
    dispatch(setActiveSession(activeSessionData));
  }, [dispatch]);
  
  /**
   * Start the timer for the active session
   */
  const startTimer = useCallback((): void => {
    if (!activeSession) {
      setLocalError('No active session');
      return;
    }
    
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    
    timerIntervalRef.current = setInterval(() => {
      const session = activeSessionRef.current;
      if (!session) return;

      dispatch(updateActiveSession({
        elapsedTime: (session.elapsedTime || 0) + 1,
        countdownTime: session.hasTimeMetric
          ? Math.max((session.countdownTime || 0) - 1, 0)
          : 0,
        didStart: true,
        isRunning: true
      }));

      // If countdown reaches zero, advance to next exercise
      if (session.hasTimeMetric && session.countdownTime === 1) {
        advanceToNextExercise();
      }
    }, 1000);

    dispatch(updateActiveSession({
      didStart: true,
      isRunning: true
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession, dispatch]);
  
  /**
   * Pause the timer for the active session
   */
  const pauseTimer = useCallback((): void => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    dispatch(updateActiveSession({
      isRunning: false
    }));
  }, [dispatch]);
  
  /**
   * Toggle the timer (play/pause)
   */
  const toggleTimer = useCallback((): void => {
    if (!activeSession) return;
    
    if (activeSession.isRunning) {
      pauseTimer();
    } else {
      startTimer();
    }
  }, [activeSession, pauseTimer, startTimer]);
  
  /**
   * Finish the active workout session
   */
  const finishWorkout = useCallback((): void => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    dispatch(updateActiveSession({
      didFinish: true,
      isRunning: false
    }));
  }, [dispatch]);
  
  /**
   * Save the current active session to the database
   */
  const saveWorkoutSession = useCallback(async (
    routineKey: string,
    notes?: string,
    tags?: string[],
    privacy: 'public' | 'private' = 'private'
  ): Promise<string | undefined> => {
    if (!activeSession || !user?.uid) {
      setLocalError('No active session or user not authenticated');
      return;
    }
    
    const sessionData: WorkoutSessionBase = {
      routineKey,
      duration: activeSession.elapsedTime,
      sets: activeSession.setExercisesResults,
      dateCreated: Date.now(),
      notes,
      tags,
      privacy
    };
    
    try {
      const sessionKey = await createSession(sessionData);
      // Reset active session after saving
      dispatch(setActiveSession(null));
      return sessionKey;
    } catch (error: any) {
      setLocalError(error.message || 'Failed to save workout session');
      return;
    }
  }, [activeSession, user?.uid, createSession, dispatch]);
  
  /**
   * Advance to next exercise in the active session
   */
  const advanceToNextExercise = useCallback((): void => {
    // Read through the ref — this is invoked from the timer interval, where
    // the render-time activeSession would be stale
    const session = activeSessionRef.current;
    if (!session) return;

    const {
      currentExerciseIndex,
      exercisesCount,
      currentSetIndex,
      setsCount,
      setExercisesResults
    } = session;
    
    let nextExerciseIndex = Math.min(currentExerciseIndex + 1, exercisesCount - 1);
    let nextSetIndex = currentSetIndex;
    
    // Check if we need to move to the next set
    if (currentExerciseIndex >= exercisesCount - 1) {
      // We're at the last exercise in the current set
      if (currentSetIndex >= setsCount - 1) {
        // This is the last exercise in the last set - finish workout
        finishWorkout();
        return;
      }
      
      // Move to next set
      nextSetIndex += 1;
      nextExerciseIndex = 0;
    }
    
    // Get information about the next exercise
    const exercisesInNextSet = setExercisesResults[nextSetIndex].exercises;
    const newExercisesCount = exercisesInNextSet.length;
    const nextExercise = exercisesInNextSet[nextExerciseIndex];
    
    // Check if the next exercise has a time metric
    const hasTimeMetric = nextExercise.metrics.some(m => m.name === 'Time');
    let countdownTime = 0;
    
    if (hasTimeMetric) {
      const timeMetric = nextExercise.metrics.find(m => m.name === 'Time');
      if (timeMetric) {
        countdownTime = Number(timeMetric.value) || 0;
      }
    }
    
    // Update the active session
    dispatch(updateActiveSession({
      currentSetIndex: nextSetIndex,
      currentExerciseIndex: nextExerciseIndex,
      exercisesCount: newExercisesCount,
      hasTimeMetric,
      countdownTime
    }));
  }, [dispatch, finishWorkout]);
  
  /**
   * Update exercise metrics in the active session
   */
  const updateExerciseMetrics = useCallback((
    setIndex: number,
    exerciseIndex: number,
    metrics: WorkoutSessionMetric[]
  ): void => {
    dispatch(updateWorkoutSessionExerciseMetrics({
      setIndex,
      exerciseIndex,
      metrics
    }));
  }, [dispatch]);
  
  return {
    // State
    sessions,
    currentSession,
    activeSession,
    recentSessions,
    isLoading: localLoading || storeLoading,
    error: localError || storeError,
    
    // Session management
    initializeSubscriptions,
    subscribeToSession,
    getSessionById,
    createSession,
    updateSession,
    deleteSession,
    loadUserSessions,
    searchSessions,
    filterSessions,
    
    // Active session control
    startWorkoutSession,
    startTimer,
    pauseTimer,
    toggleTimer,
    finishWorkout,
    saveWorkoutSession,
    advanceToNextExercise,
    updateExerciseMetrics,
    
    // Cleanup
    resetSessions: () => dispatch(resetSessions())
  };
};